import { useCallback, useMemo } from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useCrearCliente } from '@/hooks/useClientes'
import { usePermissions } from '@/hooks/usePermissions'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useEtiquetasCliente, useReconciliarEtiquetasCliente } from '@/core/hooks/useEtiquetasCliente'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { useSupabase } from '@/lib/supabase-context'
import { serviciosService } from '@/services/serviciosService'
import { generarContratoBase64, DatosEstudioIncompletosError } from '@/lib/contratoHelpers'
import { generarEstadoCuentaBase64 } from '@/lib/estadoCuentaBase64'
import type { ClienteFormValues } from '@/components/clientes/wizard/clienteSchema'
import type { ClienteCompletoInput, ServicioAlta } from '@/services/clientesService'
import type { Permisos } from '@/types/roles'
import type { Database } from '@/types/database'

type PermisosAlta = Pick<
  Permisos,
  'asignarResponsablesCliente' | 'editarHonorariosCliente' | 'verHonorariosCliente'
>

/**
 * Convierte los valores del wizard en el input de persona, cliente y faceta contable.
 * Sin permiso de asignar responsables, el input no trae responsables.
 * Sin permiso de ver y de editar honorarios, no trae honorarios.
 */
export const armarClienteInput = (
  data: ClienteFormValues,
  { organizacionId, createdBy, puede }: { organizacionId: string | null; createdBy: string | null; puede: PermisosAlta }
): ClienteCompletoInput => ({
  organizacion_id: organizacionId,
  // Datos de persona
  tipo_persona: data.tipo_persona,
  nombre: data.nombre,
  apellido: data.apellido ?? undefined,
  nombre_fantasia: data.nombre_fantasia,
  cuit: data.cuit,
  email: data.email,
  telefono: data.telefono,
  // Domicilio
  domicilio_calle: data.domicilio_calle,
  domicilio_numero: data.domicilio_numero,
  domicilio_piso: data.domicilio_piso,
  domicilio_depto: data.domicilio_depto,
  domicilio_cp: data.domicilio_cp,
  domicilio_localidad: data.domicilio_localidad,
  domicilio_provincia: data.domicilio_provincia,
  domicilio_barrio: data.domicilio_barrio,
  domicilio_referencia: data.domicilio_referencia,
  // Administrador (sucesión indivisa)
  admin_cuit: data.admin_cuit,
  admin_nombre: data.admin_nombre,
  // Datos de cliente
  estado: data.estado?.toLowerCase() as 'activo' | 'inactivo' | 'suspendido',
  fecha_alta: data.cliente_desde,
  responsables: puede.asignarResponsablesCliente ? data.responsables || [] : undefined,
  descripcion: data.descripcion_tarea || null,
  // Datos de faceta contable
  condicion_iva: data.condicion_iva,
  categoria_monotributo: data.categoria_monotributo,
  actividad_principal: data.actividad || data.actividades_economicas?.[0]?.descripcion || null,
  codigo_actividad_afip: data.codigo_actividad_afip || data.actividades_economicas?.[0]?.codigo || null,
  actividades_economicas: data.actividades_economicas,
  cierre_ejercicio: data.cierre_ejercicio,
  honorarios_mensuales: puede.editarHonorariosCliente && puede.verHonorariosCliente ? data.honorarios_mensuales : undefined,
  clave_fiscal: null,
  provincia_principal: data.provincia,
  impuestos_inscriptos: null,
  regimen_iibb: data.regimen_iibb,
  impuestos_liquidar: data.impuestos_liquidar || [],
  // Datos de Inscripción RPC (solo sociedades)
  inscripcion_numero: data.inscripcion_numero || null,
  inscripcion_fojas: data.inscripcion_fojas || null,
  inscripcion_tomo: data.inscripcion_tomo || null,
  inscripcion_protocolo: data.inscripcion_protocolo || null,
  inscripcion_fecha: data.inscripcion_fecha || null,
  inscripcion_ano: data.inscripcion_ano || null,
  created_by: createdBy,
})

/** Los servicios del wizard que tienen servicio y precio mayor que cero, con la forma que guarda la base. */
export const serviciosConPrecioValido = (servicios: ClienteFormValues['serviciosConPrecio']): ServicioAlta[] =>
  (servicios ?? [])
    .filter((servicio) => servicio.servicio_id && servicio.precio > 0)
    .map((servicio) => ({
      servicio_id: servicio.servicio_id,
      precio: servicio.precio,
      frecuencia: servicio.frecuencia,
      mes_inicio: servicio.mes_inicio,
    }))

/** Asigna al cliente, uno por uno, los servicios del wizard que tienen servicio y precio mayor que cero. */
export const asignarServiciosConPrecio = async (
  supabase: SupabaseClient<Database>,
  clienteId: string,
  servicios: ClienteFormValues['serviciosConPrecio']
) => {
  for (const servicio of serviciosConPrecioValido(servicios)) {
    await serviciosService.asignarServicioCliente(supabase, { cliente_id: clienteId, ...servicio })
  }
}

/**
 * Lleva las etiquetas del cliente de `idsActuales` a `idsFinal`.
 * Un fallo no se propaga: el cliente ya se guardó, así que solo se avisa con un toast.
 */
export function useGuardarEtiquetasCliente() {
  const reconciliarEtiquetas = useReconciliarEtiquetasCliente()
  const { data: catalogoEtiquetas = [] } = useEtiquetasCliente()
  const nombrePorIdEtiqueta = useMemo(
    () => new Map(catalogoEtiquetas.map((e) => [e.id, e.nombre])),
    [catalogoEtiquetas]
  )

  return useCallback(
    async ({ clienteId, idsActuales, idsFinal, clienteNombre }: {
      clienteId: string
      idsActuales: string[]
      idsFinal: string[]
      clienteNombre: string
    }) => {
      if (idsFinal.length === 0 && idsActuales.length === 0) return
      try {
        const { fallos } = await reconciliarEtiquetas.mutateAsync({
          clienteId,
          idsActuales,
          idsFinal,
          clienteNombre,
          nombresPorId: nombrePorIdEtiqueta,
        })
        if (fallos > 0) {
          toast.warning(
            `El cliente se guardó, pero ${fallos} etiqueta${fallos > 1 ? 's' : ''} no se ${fallos > 1 ? 'pudieron' : 'pudo'} actualizar`
          )
        }
      } catch (errEtiquetas) {
        console.error('[etiquetas] error al reconciliar etiquetas del cliente:', errEtiquetas)
        toast.warning('El cliente se guardó, pero no se pudieron actualizar sus etiquetas')
      }
    },
    [reconciliarEtiquetas, nombrePorIdEtiqueta]
  )
}

/**
 * Envía la bienvenida al terminar el alta, cuando el usuario la confirma: arma el contrato y, con
 * `adjuntarEstadoCuenta` (por defecto sí) y los permisos `enviarEstadoCuenta` y `verHonorariosCliente`, el
 * estado de cuenta con lo que ya quedó guardado, y llama a la function de envío. Sin el estado de cuenta, la function no lo
 * menciona en el mail.
 * Sin email, o si falla el PDF o el envío, no revierte nada — el alta ya está hecha — y solo avisa
 * con un toast que se puede reenviar desde la ficha del cliente.
 */
export function useEnviarBienvenidaAutomatica() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { getAccessTokenSilently } = useAuth0()
  const { data: configEstudio } = useConfiguracionEstudio()
  const { puede } = usePermissions()
  const puedeVerHonorarios = puede.verHonorariosCliente
  const puedeAdjuntarEstadoCuenta = puede.enviarEstadoCuenta && puede.verMovimientos

  return useCallback(
    async (
      clienteId: string,
      email: string | null | undefined,
      { adjuntarEstadoCuenta = true }: { adjuntarEstadoCuenta?: boolean } = {}
    ) => {
      if (!email) {
        toast.warning('El cliente se guardó, pero no tiene email: la bienvenida no se envió', {
          description: 'Podés enviarla desde la ficha del cliente.',
        })
        return
      }
      if (!supabase) return
      try {
        const adjuntaEstadoCuenta = adjuntarEstadoCuenta && puedeAdjuntarEstadoCuenta
        // El estado de cuenta se lee dentro de la organización activa. Sin ella no se manda una
        // bienvenida distinta de la pedida: cae en el aviso de que no se pudo enviar.
        if (adjuntaEstadoCuenta && !organizacionId) throw new Error('No organization')
        const [contratoBase64, estadoCuentaBase64] = await Promise.all([
          generarContratoBase64({ supabase, clienteId, configEstudio: configEstudio ?? null, puedeVerHonorarios }),
          adjuntaEstadoCuenta && organizacionId
            ? generarEstadoCuentaBase64({ supabase, clienteId, organizacionId, configEstudio: configEstudio ?? null })
            : undefined,
        ])
        const token = await getAccessTokenSilently()
        const response = await fetch('/.netlify/functions/enviar-bienvenida-cliente', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            cliente_id: clienteId,
            contrato_base64: contratoBase64,
            estado_cuenta_base64: estadoCuentaBase64,
          }),
        })
        if (!response.ok) throw new Error('La function de bienvenida devolvió un error')
        toast.success('Bienvenida enviada', { description: `Se envió a ${email}` })
      } catch (err) {
        console.error('[bienvenida] error al enviar la bienvenida automática:', err)
        if (err instanceof DatosEstudioIncompletosError) {
          toast.warning('El cliente se guardó, pero no se pudo generar el contrato', { description: err.message })
        } else {
          toast.warning('El cliente se guardó, pero la bienvenida no se pudo enviar', {
            description: 'Podés reenviarla desde la ficha del cliente.',
          })
        }
      }
    },
    [supabase, organizacionId, getAccessTokenSilently, configEstudio, puedeVerHonorarios, puedeAdjuntarEstadoCuenta]
  )
}

/**
 * Alta de un cliente desde el wizard: crea persona, cliente, faceta, servicios y deuda inicial en
 * una sola llamada de `useCrearCliente`, y asigna las etiquetas.
 * Los servicios se mandan solo con permiso de ver y de editar honorarios. Las deudas solo con
 * permiso de crear movimientos, que la RPC exige para cualquier deuda.
 * `onCreado` corre apenas existe el cliente, antes de las etiquetas.
 * Un error del alta se propaga. Uno de las etiquetas solo se avisa.
 * `darDeAlta` no envía la bienvenida: quien lo llama la confirma con el usuario y la manda con
 * `enviarBienvenida`.
 */
export function useAltaCliente() {
  const crearCliente = useCrearCliente()
  const guardarEtiquetas = useGuardarEtiquetasCliente()
  const enviarBienvenida = useEnviarBienvenidaAutomatica()
  const { organizacionId, usuario } = useOrganizacion()
  const { puede } = usePermissions()

  const darDeAlta = useCallback(
    async (
      data: ClienteFormValues,
      { onCreado }: { onCreado?: (clienteId: string, input: ClienteCompletoInput) => void } = {}
    ) => {
      const conHonorarios = puede.editarHonorariosCliente && puede.verHonorariosCliente
      const input: ClienteCompletoInput = {
        ...armarClienteInput(data, { organizacionId, createdBy: usuario?.email || null, puede }),
        servicios: conHonorarios ? serviciosConPrecioValido(data.serviciosConPrecio) : [],
        deudas: puede.crearMovimiento ? data.deudasIniciales ?? [] : [],
      }
      const nuevoCliente = await crearCliente.mutateAsync(input)
      const clienteId = nuevoCliente.id
      onCreado?.(clienteId, input)

      await guardarEtiquetas({ clienteId, idsActuales: [], idsFinal: data.etiquetas ?? [], clienteNombre: input.nombre })

      return { clienteId, input }
    },
    [crearCliente, guardarEtiquetas, organizacionId, usuario, puede]
  )

  return { darDeAlta, enviarBienvenida, isPending: crearCliente.isPending }
}
